import {
  Autocomplete,
  AutocompleteOption,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemContent,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  useGetAllPersonsQuery,
  type IPersonListItem,
} from '../../../api/generated/person-controller/person-controller.ts';
import {
  getGetScreeningTracingIndividualsQueryKey,
  useCreateScreeningTracingContact,
} from '../../../api/generated/screening-tracing-controller/screening-tracing-controller.ts';
import { getGetPatientsSummaryQueryKey } from '../../../api/generated/summary-controller/summary-controller.ts';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { CreatePersonData } from '../../masterdata/components/CreatePersonData.tsx';

interface FormValues {
  contactPerson: IPersonListItem | null;
}

const defaultValues: FormValues = {
  contactPerson: null,
};

const CREATE_PERSON_OPTION_ID = '__create_person__';

type CreatePersonOption = {
  id: typeof CREATE_PERSON_OPTION_ID;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: null;
  created: null;
  phone: null;
  email: null;
};

type ContactPersonOption = IPersonListItem | CreatePersonOption;

const CREATE_PERSON_OPTION: CreatePersonOption = {
  id: CREATE_PERSON_OPTION_ID,
  firstName: 'Person erfassen',
  lastName: '',
  birthDate: '',
  gender: null,
  created: null,
  phone: null,
  email: null,
};

export function ScreeningTracingContactDialog({
  screeningCaseId,
  open,
  onClose,
}: {
  screeningCaseId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [isCreatePersonDialogOpen, setIsCreatePersonDialogOpen] = useState(false);
  const [createdPersons, setCreatedPersons] = useState<IPersonListItem[]>([]);

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors, isDirty: isFormDirty, isValid },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
  });

  const { data: allPersons = [], isLoading: isPersonsLoading } = useGetAllPersonsQuery(open);
  const contactPersonOptions = useMemo<ContactPersonOption[]>(() => {
    const mergedPersons = [...allPersons, ...createdPersons];
    const uniquePersons = mergedPersons.filter(
      (person, index, persons) => index === persons.findIndex((candidate) => candidate.id === person.id),
    );

    return [CREATE_PERSON_OPTION, ...uniquePersons];
  }, [allPersons, createdPersons]);

  const createScreeningTracingContactMutation = useCreateScreeningTracingContact({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetScreeningTracingIndividualsQueryKey({ screeningCaseId }),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetPatientsSummaryQueryKey(),
          }),
        ]);
        toast.success('Kontakt wurde erstellt');
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Erfassen des Kontakts ist ein Fehler aufgetreten');
      },
    },
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const handleOnClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <>
        <ModalDialog sx={{ width: 'min(720px, 95vw)', p: 0 }}>
          <Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
              <Typography level="h4">Screening-Person hinzufügen</Typography>
              <ModalClose />
            </Stack>

            <form
              onSubmit={handleSubmit((data) => {
                createScreeningTracingContactMutation.mutate({
                  data: {
                    screeningCaseId,
                    personId: data.contactPerson!.id,
                  },
                });
              })}>
              <Stack gap={2} sx={{ px: 3, pb: 3 }}>
                <FormControl error={!!errors.contactPerson} required>
                  <FormLabel>Screening-Person</FormLabel>
                  <Controller
                    name="contactPerson"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                      <Autocomplete<ContactPersonOption, false, false, false>
                        value={value}
                        options={contactPersonOptions}
                        onChange={(_, newValue) => {
                          if (newValue?.id === CREATE_PERSON_OPTION_ID) {
                            setIsCreatePersonDialogOpen(true);
                            return;
                          }

                          onChange(newValue);
                        }}
                        filterOptions={(options, { inputValue }) => {
                          const normalizedInput = inputValue.trim().toLowerCase();
                          const personOptions = options.filter((option) => option.id !== CREATE_PERSON_OPTION_ID);

                          if (!normalizedInput) {
                            return [CREATE_PERSON_OPTION, ...personOptions];
                          }

                          const filteredPersons = personOptions.filter((option: ContactPersonOption) =>
                            `${option.firstName ?? ''} ${option.lastName ?? ''} ${option.email ?? ''} ${option.phone ?? ''}`
                              .toLowerCase()
                              .includes(normalizedInput),
                          );

                          return [CREATE_PERSON_OPTION, ...filteredPersons];
                        }}
                        getOptionLabel={(option) =>
                          option.id === CREATE_PERSON_OPTION_ID
                            ? 'Person erfassen'
                            : `${option.firstName ?? ''} ${option.lastName ?? ''}`.trim()
                        }
                        isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
                        renderOption={(props, option) => {
                          if (option.id === CREATE_PERSON_OPTION_ID) {
                            return (
                              <AutocompleteOption
                                {...props}
                                key={CREATE_PERSON_OPTION_ID}
                                onClick={() => setIsCreatePersonDialogOpen(true)}>
                                <Typography level="title-sm">Neue Person erfassen</Typography>
                              </AutocompleteOption>
                            );
                          }

                          return (
                            <AutocompleteOption {...props} key={option.id}>
                              <ListItemContent>
                                <Typography level="title-sm">
                                  {option.firstName} {option.lastName}
                                </Typography>
                              </ListItemContent>
                            </AutocompleteOption>
                          );
                        }}
                        placeholder="Screening-Person auswählen"
                        noOptionsText="Keine Screening-Personen gefunden"
                        disabled={isPersonsLoading}
                      />
                    )}
                  />
                  {errors.contactPerson ? <FormHelperText>Bitte Screening-Person auswählen</FormHelperText> : null}
                </FormControl>

                <FormActionButtons
                  onAbortClick={handleOnClose}
                  disableSubmit={!isFormDirty || !isValid}
                  isLoading={createScreeningTracingContactMutation.isPending}
                />
              </Stack>
            </form>
          </Stack>
        </ModalDialog>

        <CreatePersonData
          open={isCreatePersonDialogOpen}
          onClose={() => setIsCreatePersonDialogOpen(false)}
          onCreated={(person) => {
            setCreatedPersons((prev) => {
              const alreadyExists = prev.some((item) => item.id === person.id);
              return alreadyExists ? prev : [...prev, person];
            });
            setValue('contactPerson', person, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            setIsCreatePersonDialogOpen(false);
            void queryClient.invalidateQueries({
              queryKey: ['persons', 'all'],
            });
          }}
        />
      </>
    </Modal>
  );
}
